import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export { BACKEND_URL };
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if this was a login attempt
      if (!error.config.url.includes('/auth/login')) {
        // Token expired or invalid
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    // Handle team-related errors
    if (error.response?.status === 403) {
      const errorCode = error.response?.data?.detail?.code;

      if (errorCode === 'TEAM_REQUIRED') {
        // User needs to join a team
        // This will be handled by TeamGate component
        console.log('Team membership required');
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => apiClient.post('/auth/signup', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  upgradeToCoach: (code) => apiClient.post('/auth/upgrade-to-coach', { code }),
  resendVerification: (email) => apiClient.post('/auth/resend-verification', { email }),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  requestPasswordOtp: () => apiClient.post('/auth/request-password-otp'),
  verifyPasswordOtp: (data) => apiClient.post('/auth/verify-password-otp', data),
};

// Teams API
export const teamsAPI = {
  create: (data) => apiClient.post('/teams', data),
  get: (teamId) => apiClient.get(`/teams/${teamId}`),
  getMinimalTeams: () => apiClient.get('/teams/minimal'),

  // Legacy join endpoint (still supported)
  join: (data) => apiClient.post('/teams/join', data),

  // New simplified join endpoints
  createJoinRequest: (teamCode) => apiClient.post('/teams/join-requests', { team_code: teamCode }),
  getMyTeamStatus: () => apiClient.get('/teams/join-requests/me'),
  listJoinRequests: (statusFilter = 'pending') =>
    apiClient.get('/teams/join-requests', { params: { status_filter: statusFilter } }),
  approveJoinRequest: (requestId) =>
    apiClient.post(`/teams/join-requests/${requestId}/approve`),
  rejectJoinRequest: (requestId) =>
    apiClient.post(`/teams/join-requests/${requestId}/reject`),

  // Old endpoints (for backward compatibility)
  getRequests: (teamId, status = 'pending') =>
    apiClient.get(`/teams/${teamId}/requests`, { params: { status } }),
  approveRequest: (teamId, requestId) =>
    apiClient.post(`/teams/${teamId}/requests/${requestId}/approve`),
  denyRequest: (teamId, requestId) =>
    apiClient.post(`/teams/${teamId}/requests/${requestId}/deny`),

  getMembers: (teamId) => apiClient.get(`/teams/${teamId}/members`),
  getPlayerCode: (teamId) => apiClient.get(`/teams/${teamId}/codes/player`),
  regenerateCode: (teamId) => apiClient.post(`/teams/${teamId}/codes/regenerate`),
  updateMemberProfile: (teamId, data) => apiClient.put(`/teams/${teamId}/members/me`, data),
  update: (teamId, data) => apiClient.put(`/teams/${teamId}`, data),
  transferOwnership: (teamId, newOwnerId) => apiClient.post(`/teams/${teamId}/transfer-ownership`, { new_owner_id: newOwnerId }),
  uploadLogo: (teamId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/teams/${teamId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Coach Invitations API
export const invitationsAPI = {
  generateCoachCode: (teamId) => apiClient.post('/invitations/coach', { team_id: teamId }),
  getCoachCodes: (teamId = null) => apiClient.get('/invitations/coach', { params: teamId ? { team_id: teamId } : {} }),
  revokeCoachCode: (codeId) => apiClient.delete(`/invitations/coach/${codeId}`),
};

// Videos API
// Videos API
export const videosAPI = {
  upload: (formData) => apiClient.post('/videos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: (teamId) => apiClient.get('/videos', { params: { team_id: teamId } }),
  get: (videoId) => apiClient.get(`/videos/${videoId}`),
  delete: (videoId) => apiClient.delete(`/social/videos/${videoId}`),
  bulkDelete: (videoIds) => apiClient.post('/social/videos/bulk-delete', videoIds),
  getUsage: (videoId) => apiClient.get(`/social/videos/${videoId}/usage`),
  toggleStar: (videoId) => apiClient.post(`/videos/${videoId}/toggle-star`),
};

// Playlists API
export const playlistsAPI = {
  create: (data) => apiClient.post('/playlists', data),
  list: () => apiClient.get('/playlists'),
  get: (playlistId) => apiClient.get(`/playlists/${playlistId}`),
  addVideo: (playlistId, videoId) => apiClient.post(`/playlists/${playlistId}/videos`, { video_id: videoId }),
  delete: (playlistId) => apiClient.delete(`/playlists/${playlistId}`),
};

// Notes API
export const notesAPI = {
  create: (data) => apiClient.post('/notes', data),
  list: (params) => apiClient.get('/notes', { params }),
  getDashboard: (limit = 5) => apiClient.get('/notes/dashboard', { params: { limit } }),
  delete: (noteId) => apiClient.delete(`/notes/${noteId}`),
};



// Analytics API
export const analyticsAPI = {
  recordEvent: (data) => apiClient.post('/analytics/engagement/event', data),
  getEngagement: (teamId, periodDays = 30) =>
    apiClient.get('/analytics/engagement', {
      params: { team_id: teamId, period_days: periodDays }
    }),
  exportCSV: (teamId, periodDays = 30) =>
    apiClient.get('/analytics/engagement/export', {
      params: { team_id: teamId, period_days: periodDays },
      responseType: 'blob'
    }),
};

// Widgets API (Banners, etc.)
export const widgetsAPI = {
  getBanner: () => apiClient.get('/widgets/banner'),
  initBannerUpload: (data) => apiClient.post('/widgets/banner/upload-init', data),
  completeBannerUpload: (data) => apiClient.post('/widgets/banner/upload-complete', data),
  deleteBanner: () => apiClient.delete('/widgets/banner'),

  // Helper function to upload banner
  uploadBanner: async (file) => {
    // Step 1: Init upload
    const initResponse = await apiClient.post('/widgets/banner/upload-init', {
      filename: file.name,
      content_type: file.type,
      file_size: file.size
    });

    // Step 2: Upload to S3
    const uploadResponse = await fetch(initResponse.data.upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file to storage');
    }

    // Step 3: Complete upload
    const completeResponse = await apiClient.post('/widgets/banner/upload-complete', {
      widget_id: initResponse.data.widget_id
    });

    return completeResponse.data;
  }
};

// League API
export const leagueAPI = {
  // Seasons
  getSeasons: () => apiClient.get('/league/seasons'),
  getActiveSeason: () => apiClient.get('/league/seasons/active'),
  getSeason: (id) => apiClient.get(`/league/seasons/${id}`),
  createSeason: (data) => apiClient.post('/league/seasons', data),
  updateSeason: (id, data) => apiClient.put(`/league/seasons/${id}`, data),
  deleteSeason: (id) => apiClient.delete(`/league/seasons/${id}`),

  // Groups
  getGroups: (seasonId) => apiClient.get('/league/groups', { params: { season_id: seasonId } }),
  createGroup: (data) => apiClient.post('/league/groups', data),
  updateGroup: (id, data) => apiClient.put(`/league/groups/${id}`, data),
  deleteGroup: (id) => apiClient.delete(`/league/groups/${id}`),
  addTeamToGroup: (groupId, data) => apiClient.post(`/league/groups/${groupId}/teams`, data),
  removeTeamFromGroup: (groupId, teamId) => apiClient.delete(`/league/groups/${groupId}/teams/${teamId}`),
  removeMemberFromGroup: (groupId, memberId) => apiClient.delete(`/league/groups/${groupId}/members/${memberId}`),

  // Matches
  getMatches: (seasonId, groupId) => apiClient.get('/league/matches', {
    params: { season_id: seasonId, group_id: groupId }
  }),
  getMatch: (id) => apiClient.get(`/league/matches/${id}`),
  createMatch: (data) => apiClient.post('/league/matches', data),
  updateMatch: (id, data) => apiClient.put(`/league/matches/${id}`, data),
  updateMatchScore: (id, data) => apiClient.put(`/league/matches/${id}/score`, data),
  deleteMatch: (id) => apiClient.delete(`/league/matches/${id}`),

  // Standings
  getStandings: (groupId) => apiClient.get(`/league/standings/${groupId}`),

  // Notifications
  getNotifications: (unreadOnly = false) => apiClient.get('/league/notifications', { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => apiClient.get('/league/notifications/unread-count'),
  markRead: (ids) => apiClient.post('/league/notifications/mark-read', { notification_ids: ids }),
  markAllRead: () => apiClient.post('/league/notifications/mark-all-read'),

  // Teams (for admin)
  getTeams: () => apiClient.get('/admin/teams')
};

// Team Announcements API
export const announcementsAPI = {
  list: (teamId) => apiClient.get('/team-notes', { params: teamId ? { team_id: teamId } : {} }),
  dashboard: (limit = 5) => apiClient.get('/team-notes/dashboard', { params: { limit } }),
  create: (data) => apiClient.post('/team-notes', data),
  update: (id, data) => apiClient.put(`/team-notes/${id}`, data),
  delete: (id) => apiClient.delete(`/team-notes/${id}`)
};

// Play of the Week API
export const playOfWeekAPI = {
  getCurrent: () => apiClient.get('/play-of-week/current'),
  getHistory: () => apiClient.get('/play-of-week/history'),
  getUserBadges: (userId) => apiClient.get(`/play-of-week/user-badges/${userId}`),
  vote: (postId) => apiClient.post('/play-of-week/vote', { post_id: postId })
};

// Social API (Feed, Profiles, Posts)
export const socialAPI = {
  // Feed
  getFeed: (limit = 20, offset = 0, followingOnly = false) => apiClient.get('/social/feed', { params: { limit, offset, following_only: followingOnly } }),

  // Posts
  // Posts
  createPost: (data) => {
    const backendHost = window.location.hostname;
    const token = localStorage.getItem('token');
    return axios.post(`${BACKEND_URL}/api/social/posts`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
  },
  getPost: (id) => apiClient.get(`/social/posts/${id}`),
  updatePost: (id, data) => apiClient.put(`/social/posts/${id}`, data),
  deletePost: (id) => apiClient.delete(`/social/posts/${id}`),

  // Likes
  getLikes: (postId) => apiClient.get(`/social/posts/${postId}/likes`),
  toggleLike: (postId) => apiClient.post(`/social/posts/${postId}/like`),
  toggleFeature: (postId) => apiClient.post(`/social/posts/${postId}/feature`),

  // Comments
  addComment: (postId, text) => apiClient.post(`/social/posts/${postId}/comments`, { text }),
  deleteComment: (commentId) => apiClient.delete(`/social/comments/${commentId}`),
  reportComment: (commentId) => apiClient.post(`/social/comments/${commentId}/report`),

  // Profiles
  getProfile: (username) => apiClient.get(`/social/profiles/${username}`),
  updateProfile: (data) => apiClient.put('/social/profiles/me', data),
  searchProfiles: (q, limit = 10) => apiClient.get('/social/profiles/search', { params: { q, limit } }),

  // Follow
  toggleFollow: (username) => apiClient.post(`/social/profiles/${username}/follow`),
  acceptFollow: (followId) => apiClient.post(`/social/follows/${followId}/accept`),
  rejectFollow: (followId) => apiClient.post(`/social/follows/${followId}/reject`),
  getFollowers: (username) => apiClient.get(`/social/profiles/${username}/followers`),
  getFollowing: (username) => apiClient.get(`/social/profiles/${username}/following`),
  toggleTeamFollow: (teamId) => apiClient.post(`/social/teams/${teamId}/follow`),

  // Experiences
  addExperience: (data) => apiClient.post('/social/profiles/me/experiences', data),
  deleteExperience: (id) => apiClient.delete(`/social/profiles/me/experiences/${id}`),

  // Videos
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/social/profiles/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadImage: (formData) => apiClient.post('/social/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    // Use direct backend URL for large file uploads (bypasses proxy timeout)
    // Using current hostname to avoid connection refused errors on different environments
    const backendHost = window.location.hostname;
    return axios.post(`${BACKEND_URL}/api/social/videos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
  },
  getMyVideos: () => apiClient.get('/social/videos/mine'),
  createVideo: (data) => apiClient.post('/social/videos', data),
  reportPost: (postId, reason) => apiClient.post(`/social/posts/${postId}/report`, null, { params: { reason } })
};

// Calendar API (Phase 1)
export const calendarAPI = {
  getEvents: (teamId, from, to) => apiClient.get('/calendar', {
    params: { team_id: teamId, from, to }
  }),
  createEvent: (teamId, data) => apiClient.post('/calendar', data, { params: { team_id: teamId } }),
  updateEvent: (eventId, data) => apiClient.put(`/calendar/${eventId}`, data),
  deleteEvent: (eventId) => apiClient.delete(`/calendar/${eventId}`),

  // Recurring events
  createRecurringEvent: (teamId, data) => apiClient.post('/calendar/recurring', data, { params: { team_id: teamId } }),
  updateRecurringEvent: (eventId, data) => apiClient.put(`/calendar/recurring/${eventId}`, data),
  deleteRecurringEvent: (eventId) => apiClient.delete(`/calendar/recurring/${eventId}`),
  deleteRecurringEventInstance: (eventId, date) => apiClient.delete(`/calendar/recurring/${eventId}/instances/${date}`),
};

// Player Stats API (Phase 1)
export const statsAPI = {
  getPlayerStats: (playerId, teamId, season) =>
    apiClient.get(`/stats/player/${playerId}`, { params: { team_id: teamId, season } }),
  getMyStats: (teamId, season) =>
    apiClient.get('/stats/me', { params: { team_id: teamId, season } }),
  createStats: (playerId, teamId, data) =>
    apiClient.post(`/stats/player/${playerId}`, data, { params: { team_id: teamId } }),
  updateStats: (statId, data) => apiClient.put(`/stats/${statId}`, data),
  deleteStats: (statId) => apiClient.delete(`/stats/${statId}`),
};

// Roster Delegation API (Phase 1)
export const rosterAPI = {
  getRoster: (teamId) => apiClient.get(`/teams/${teamId}/members`),
  grantDelegation: (teamId, userId) => apiClient.post(`/teams/${teamId}/roster/${userId}/delegate`),
  revokeDelegation: (teamId, userId) => apiClient.delete(`/teams/${teamId}/roster/${userId}/delegate`),
  updateMemberPermissions: (teamId, userId, data) => apiClient.put(`/teams/${teamId}/members/${userId}/permissions`, data),
  removeMember: (teamId, userId) => apiClient.delete(`/teams/${teamId}/members/${userId}`),
  createPositionRequest: (teamId, positions) => apiClient.post(`/teams/${teamId}/requests/position`, { positions }),
  transferOwnership: (teamId, newOwnerId) => apiClient.post(`/teams/${teamId}/transfer-ownership`, { new_owner_id: newOwnerId }),
};

// Admin Panel API (Phase 1)
export const adminAPI = {
  getTeams: (includeDeleted = false) => apiClient.get('/admin/teams', {
    params: { include_counts: true, include_deleted: includeDeleted }
  }),
  getTeam: (teamId) => apiClient.get(`/admin/teams/${teamId}`),
  createTeam: (data) => apiClient.post('/admin/teams', data),
  updateTeam: (teamId, data) => apiClient.patch(`/admin/teams/${teamId}`, data),
  deleteTeam: (teamId, force = false) => apiClient.delete(`/admin/teams/${teamId}`, { params: { force } }),
  regenerateCode: (teamId) => apiClient.post(`/admin/teams/${teamId}/regenerate-code`),
  getAllUsers: (params) => apiClient.get('/admin/users', { params }),
  getTeamStorage: () => apiClient.get('/admin/teams/storage'),

  // Events
  getActiveEvent: () => apiClient.get('/admin/events/active'),
  getAllEvents: () => apiClient.get('/admin/events'),
  startEvent: (type, durationDays, params = {}) => apiClient.post('/admin/events/start', null, {
    params: {
      event_type: type,
      duration_days: durationDays,
      ...params
    }
  }),
  endEvent: (eventId) => apiClient.post(`/admin/events/${eventId}/end`),
  cancelEvent: (eventId) => apiClient.post(`/admin/events/${eventId}/cancel`),
  getReports: (statusFilter = 'pending') => apiClient.get('/admin/reports', { params: { status_filter: statusFilter } }),
  getTeamStorage: () => apiClient.get('/admin/teams/storage'),
  updateTeamStorageLimit: (teamId, limitGb) => apiClient.put(`/admin/teams/${teamId}/storage-limit`, { limit_gb: limitGb }),
  updateTeamPlan: (teamId, plan) => apiClient.patch(`/admin/teams/${teamId}/plan`, { plan }),

  // Marketplace admin
  updateUserMembershipType: (userId, membershipType) => apiClient.patch(`/admin/users/${userId}/membership-type`, { membership_type: membershipType }),
  updateUserMarketplaceLimit: (userId, limit) => apiClient.patch(`/admin/users/${userId}/marketplace-limit`, { limit }),
  updateUserPartnerStatus: (userId, isPartner) => apiClient.patch(`/admin/users/${userId}/partner-status`, { is_partner: isPartner }),
};

// Marketplace API
export const marketplaceAPI = {
  getListings: (params = {}) => apiClient.get('/marketplace/listings', { params }),
  getMyListings: (statusFilter) => apiClient.get('/marketplace/my-listings', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  createListing: (data) => apiClient.post('/marketplace/listings', data),
  updateListing: (id, data) => apiClient.patch(`/marketplace/listings/${id}`, data),
  deleteListing: (id) => apiClient.delete(`/marketplace/listings/${id}`),
  getMyLimit: () => apiClient.get('/marketplace/my-limit'),
  getUserListings: (username) => apiClient.get(`/marketplace/user/${username}/listings`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/marketplace/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  recordView: (listingId) => apiClient.post(`/marketplace/listings/${listingId}/view`),
};

// Team Profile API
export const teamProfileAPI = {
  // Profile
  getProfile: (teamId) => apiClient.get(`/teams/${teamId}/profile`),
  updateProfile: (teamId, data) => apiClient.put(`/teams/${teamId}/profile`, data),

  // Trophies
  addTrophy: (teamId, data) => apiClient.post(`/teams/${teamId}/trophies`, data),
  deleteTrophy: (teamId, trophyId) => apiClient.delete(`/teams/${teamId}/trophies/${trophyId}`),

  // Calendar/Events
  getEvents: (teamId, year, month) => apiClient.get(`/teams/${teamId}/events`, { params: { year, month } }),
  addEvent: (teamId, data) => apiClient.post(`/teams/${teamId}/events`, data),
  updateEvent: (teamId, eventId, data) => apiClient.put(`/teams/${teamId}/events/${eventId}`, data),
  deleteEvent: (teamId, eventId) => apiClient.delete(`/teams/${teamId}/events/${eventId}`),

  // Team Posts
  getPosts: (teamId, limit = 20, offset = 0) => apiClient.get(`/teams/${teamId}/posts`, { params: { limit, offset } }),
  createPost: (teamId, data) => apiClient.post(`/teams/${teamId}/posts`, data),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => apiClient.post('/feedback', data),
  getMyFeedback: () => apiClient.get('/feedback'),
  getAllFeedback: (status) => apiClient.get('/admin/feedback', { params: { status } }), // Admin access
};

// Training API
// Training API
export const trainingAPI = {
  // Session Management
  createSession: (teamId, data) =>
    apiClient.post('/training/sessions', data, { params: { team_id: teamId } }),
  listSessions: (teamId) =>
    apiClient.get('/training/sessions', { params: { team_id: teamId } }),
  getSession: (sessionId) => apiClient.get(`/training/sessions/${sessionId}`),
  updateSession: (sessionId, data) => apiClient.put(`/training/sessions/${sessionId}`, data),
  deleteSession: (sessionId) => apiClient.delete(`/training/sessions/${sessionId}`),

  // Video Management
  addVideo: (data) => apiClient.post('/training/videos', data),
  uploadVideo: (sessionId, formData) => apiClient.post(`/training/sessions/${sessionId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteVideo: (sessionId, videoId) =>
    apiClient.delete(`/training/sessions/${sessionId}/videos/${videoId}`),
  updateVideo: (sessionId, videoId, data) =>
    apiClient.put(`/training/sessions/${sessionId}/videos/${videoId}`, data),
  listSessionVideos: (sessionId) =>
    apiClient.get(`/training/sessions/${sessionId}/videos`),
  listTeamVideos: (teamId) =>
    apiClient.get(`/training/team/${teamId}/videos`),

  // Annotations
  createAnnotation: (sessionId, videoId, data) =>
    apiClient.post(`/training/sessions/${sessionId}/videos/${videoId}/annotations`, data),
  listAnnotations: (sessionId, videoId) =>
    apiClient.get(`/training/sessions/${sessionId}/videos/${videoId}/annotations`),
  deleteAnnotation: (sessionId, videoId, annotationId) =>
    apiClient.delete(`/training/sessions/${sessionId}/videos/${videoId}/annotations/${annotationId}`),

  // Attendance & Upcoming
  getUpcoming: () => apiClient.get('/training/upcoming'),
  getAttendanceStats: () => apiClient.get('/training/stats'),
  getMyAttendance: () => apiClient.get('/training/attendance/me'),
  submitAttendance: (data) => apiClient.post('/training/attendance', data),
  getNextAttendanceSheet: () => apiClient.get('/training/next-attendance-sheet'),
};

// Messages API
export const messagesAPI = {
  getConversations: () => apiClient.get('/messages/conversations'),
  getMessages: (userId, params) => apiClient.get(`/messages/${userId}`, { params }),
  sendMessage: (userId, data) => apiClient.post(`/messages/${userId}`, data),
  markAsRead: (userId) => apiClient.post(`/messages/${userId}/read`),
  getUnreadCount: () => apiClient.get('/messages/unread/count')
};

// Notifications API
export const notificationsAPI = {
  list: (unreadOnly = false, limit = 50, offset = 0) =>
    apiClient.get('/notifications', { params: { unread_only: unreadOnly, limit, offset } }),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  markRead: (id) => apiClient.post(`/notifications/${id}/read`),
  markAllRead: () => apiClient.post('/notifications/read-all'),
  delete: (id) => apiClient.delete(`/notifications/${id}`),
  registerFCMToken: (token, deviceType = 'web') =>
    apiClient.post('/notifications/tokens', { token, device_type: deviceType }),
};

export { apiClient };
export default apiClient;
